import { useI18n } from '@ecom/i18n';
import { Button, Field, Input, Select } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { useCategoriesQuery } from '../api';
import { productFormSchema, type ProductFormValues } from '../schemas';

import { slugify } from '@/features/store/slug';
import { whenDefined } from '@/lib/optional-props';

export function ProductForm({
  defaultValues,
  onSubmit,
  isSubmitting,
  submitLabel,
}: {
  defaultValues?: Partial<ProductFormValues>;
  onSubmit: (values: ProductFormValues) => void;
  isSubmitting: boolean;
  submitLabel: string;
}): ReactElement {
  const { t } = useI18n();
  const categoriesQuery = useCategoriesQuery();

  const statusOptions = [
    { value: 'draft', label: t('merchant.products.form.statusDraftOption') },
    { value: 'active', label: t('merchant.products.form.statusActiveOption') },
    { value: 'inactive', label: t('merchant.products.status.inactive') },
  ];

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: { status: 'draft', ...defaultValues },
  });
  // A new product's slug follows its name (`StoreForm`'s rule) until the merchant types one; an existing slug is a
  // live public address and is never rewritten from a rename.
  const slugFollowsName = useRef((defaultValues?.slug ?? '') === '');

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      noValidate
      className="flex flex-col gap-6"
    >
      <section className="ecom-card flex flex-col gap-4 shadow-card">
        <h2 className="font-display text-base font-semibold text-ink">
          {t('merchant.products.form.generalInfo')}
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('merchant.products.form.nameLabel')}
            {...whenDefined('error', errors.name?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                {...register('name', {
                  onChange: (event: { target: { value: string } }) => {
                    if (slugFollowsName.current) {
                      setValue('slug', slugify(event.target.value), {
                        shouldValidate: isSubmitted,
                      });
                    }
                  },
                })}
                autoFocus
              />
            )}
          </Field>

          <Field
            label={t('merchant.store.form.slugLabel')}
            hint={t('merchant.products.form.slugHint')}
            {...whenDefined('error', errors.slug?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                {...register('slug', {
                  onChange: () => {
                    slugFollowsName.current = false;
                  },
                })}
              />
            )}
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('merchant.products.form.skuLabel')}
            {...whenDefined('error', errors.sku?.message)}
          >
            {(bind) => <Input {...bind} {...register('sku')} />}
          </Field>

          <Field
            label={t('merchant.products.form.categoryLabel')}
            optional
            {...whenDefined('error', errors.categoryId?.message)}
          >
            {(bind) => (
              <Select
                {...bind}
                {...register('categoryId')}
                options={(categoriesQuery.data ?? []).map((category) => ({
                  value: category.id,
                  label: category.name,
                }))}
                placeholder={
                  categoriesQuery.isPending
                    ? t('merchant.products.form.categoryLoading')
                    : t('merchant.products.form.categoryNone')
                }
              />
            )}
          </Field>
        </div>

        <Field
          label={t('merchant.products.form.shortDescriptionLabel')}
          optional
          {...whenDefined('error', errors.shortDescription?.message)}
        >
          {(bind) => <Input {...bind} {...register('shortDescription')} />}
        </Field>

        <Field
          label={t('merchant.products.form.longDescriptionLabel')}
          optional
          {...whenDefined('error', errors.longDescription?.message)}
        >
          {(bind) => (
            <textarea {...bind} {...register('longDescription')} rows={5} className="ecom-input" />
          )}
        </Field>

        <Field
          label={t('merchant.products.form.brandLabel')}
          optional
          {...whenDefined('error', errors.brand?.message)}
        >
          {(bind) => <Input {...bind} {...register('brand')} />}
        </Field>
      </section>

      <section className="ecom-card flex flex-col gap-4 shadow-card">
        <h2 className="font-display text-base font-semibold text-ink">
          {t('merchant.products.form.priceAndStatus')}
        </h2>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label={t('merchant.products.form.priceLabel')}
            {...whenDefined('error', errors.price?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                type="number"
                step="0.001"
                min="0"
                {...register('price', { valueAsNumber: true })}
              />
            )}
          </Field>

          <Field
            label={t('merchant.products.form.compareAtPriceLabel')}
            optional
            hint={t('merchant.products.form.compareAtPriceHint')}
            {...whenDefined('error', errors.compareAtPrice?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                type="number"
                step="0.001"
                min="0"
                {...register('compareAtPrice', { valueAsNumber: true })}
              />
            )}
          </Field>

          <Field
            label={t('merchant.orders.table.colStatus')}
            {...whenDefined('error', errors.status?.message)}
          >
            {(bind) => (
              <Select {...bind} {...register('status')} options={statusOptions} required />
            )}
          </Field>
        </div>
      </section>

      <section className="ecom-card flex flex-col gap-4 shadow-card">
        <h2 className="font-display text-base font-semibold text-ink">
          {t('merchant.store.form.seo')}
        </h2>

        <Field
          label={t('merchant.store.form.seoTitleLabel')}
          optional
          {...whenDefined('error', errors.seoTitle?.message)}
        >
          {(bind) => <Input {...bind} {...register('seoTitle')} />}
        </Field>

        <Field
          label={t('merchant.store.form.seoDescriptionLabel')}
          optional
          {...whenDefined('error', errors.seoDescription?.message)}
        >
          {(bind) => <Input {...bind} {...register('seoDescription')} />}
        </Field>
      </section>

      <div>
        <Button type="submit" loading={isSubmitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
